import React from 'react';
import { ThemeProvider } from '@emotion/react';
import { extendTheme, ThemeTypes } from '@polestar/component-warehouse-react/themes/Theme';
import { useThemes } from '@polestar/component-warehouse-react/contexts/themes';
import { useThemeType } from '@polestar/component-warehouse-react/hooks/useTheme';
/**
 * Force the dark theme to all its children
 *
 * @param children
 * @param overrides optional theme settings to be added onto the dark theme
 * @returns <ThemeProvider> with children
 */
export const DarkThemeProvider = ({ children, overrides }) => {
    const { [ThemeTypes.dark]: dark } = useThemes();
    const mergedTheme = overrides ? extendTheme(dark, overrides) : dark;
    return React.createElement(ThemeProvider, { theme: mergedTheme }, children);
};
/**
 * Force the light theme to all its children
 *
 * @param children
 * @param overrides optional theme settings to be added onto the light theme
 * @returns <ThemeProvider> with children
 */
export const LightThemeProvider = ({ children, overrides }) => {
    const { [ThemeTypes.light]: light } = useThemes();
    const mergedTheme = overrides ? extendTheme(light, overrides) : light;
    return React.createElement(ThemeProvider, { theme: mergedTheme }, children);
};
/**
 * Invert the (light / dark) theme to all its children
 *
 * @param children
 * @param overrides optional theme settings to be added onto the dark theme
 * @returns <ThemeProvider> with children
 */
export const InvertedThemeProvider = ({ children, overrides }) => {
    const themeType = useThemeType();
    const themes = useThemes();
    const theme = themes[themeType === ThemeTypes.light ? ThemeTypes.dark : ThemeTypes.light];
    const mergedTheme = overrides ? extendTheme(theme, overrides) : theme;
    return React.createElement(ThemeProvider, { theme: mergedTheme }, children);
};
/**
 * When we switch from LightThemeProvider to DarkThemeProvider there is no transition because it's a different component
 * This is why this component is needed for some cases where the transition is needed (like Header switching from light to dark)
 *
 * @internal
 * @param children
 * @param overrides optional theme settings to be added onto the dark theme
 * @returns <ThemeProvider> with children
 */
export const SwitchableThemeProvider = ({ children, type, overrides, }) => {
    const themes = useThemes();
    const theme = themes[type];
    const mergedTheme = overrides ? extendTheme(theme, overrides) : theme;
    return React.createElement(ThemeProvider, { theme: mergedTheme }, children);
};
